import { DIRECTION_LETTERS, DIRECTION_BEARINGS } from './courseConstants.js';
import { parseMarkSequence, parseBulkLine } from './courseParser.js';

/**
 * Provider-agnostic LLM wrapper for parsing freeform course text.
 *
 * Default provider: Firebase AI (Vertex AI in Firebase).
 * Fallback: deterministic parseBulkLine for well-formed input.
 * Mock-friendly: pass a custom provider via options.
 */

let _defaultProvider = null;

/**
 * Set a module-level default provider (useful for testing or app-wide config).
 */
export function setDefaultProvider(provider) {
  _defaultProvider = provider;
}

/**
 * Create a Firebase AI provider from a Firebase app instance.
 */
export async function createFirebaseAIProvider(app) {
  const { getAI, getGenerativeModel, GoogleAIBackend } = await import('firebase/ai');
  const ai = getAI(app, { backend: new GoogleAIBackend() });
  const model = getGenerativeModel(ai, { model: 'gemini-2.0-flash' });
  return {
    async generateContent(prompt) {
      const result = await model.generateContent(prompt);
      return result.response.text();
    },
  };
}

const SYSTEM_PROMPT = `You are a sailing course card parser. Extract course entries from the text below.

Each course has:
- directionCode: one of ${DIRECTION_LETTERS.join(', ')} (16-point compass, skips I and O)
- courseNumber: 1-8
- markSequence: uppercase letters representing marks to round (e.g., "ADEF")

The course name combines directionCode + courseNumber, e.g., "A1", "B3", "N7".

Return ONLY a JSON array. Each element:
{"directionCode": "A", "courseNumber": 1, "markSequence": "ADEF"}

If a line cannot be parsed, include it as:
{"error": "could not parse", "line": "the original line"}

Do not include any text outside the JSON array.`;

/**
 * Build the LLM prompt from input text.
 */
function buildPrompt(text) {
  return `${SYSTEM_PROMPT}\n\nInput text:\n${text}`;
}

/**
 * Validate and convert an LLM-parsed entry into a course object.
 * Returns null if invalid.
 */
function validateEntry(entry) {
  if (entry.error) return null;

  const dir = (entry.directionCode || '').toUpperCase();
  if (!DIRECTION_LETTERS.includes(dir)) return null;

  const num = parseInt(entry.courseNumber, 10);
  if (isNaN(num) || num < 1 || num > 8) return null;

  const seq = entry.markSequence || '';
  const marks = parseMarkSequence(seq);
  if (marks.length === 0) return null;

  return {
    id: crypto.randomUUID(),
    directionCode: dir,
    courseNumber: num,
    markIds: marks.map((m) => m.id),
    markDirections: marks.map((m) => m.rounding),
    bearing: Math.round(DIRECTION_BEARINGS[dir]),
    distance: null,
  };
}

/**
 * Parse LLM response text into courses and errors.
 */
function parseLLMResponse(responseText) {
  const courses = [];
  const errors = [];

  // Extract JSON array from response (may have markdown fences)
  const jsonMatch = responseText.match(/\[[\s\S]*\]/);
  if (!jsonMatch) {
    errors.push({ error: 'LLM response did not contain a JSON array', raw: responseText });
    return { courses, errors };
  }

  let entries;
  try {
    entries = JSON.parse(jsonMatch[0]);
  } catch (e) {
    errors.push({ error: 'Failed to parse LLM JSON response', raw: jsonMatch[0] });
    return { courses, errors };
  }

  if (!Array.isArray(entries)) {
    errors.push({ error: 'LLM response was not an array', raw: responseText });
    return { courses, errors };
  }

  for (const entry of entries) {
    if (entry.error) {
      errors.push({ error: entry.error, line: entry.line || '' });
      continue;
    }
    const course = validateEntry(entry);
    if (course) {
      courses.push(course);
    } else {
      errors.push({ error: 'Invalid course data from LLM', raw: JSON.stringify(entry) });
    }
  }

  return { courses, errors };
}

/**
 * Parse freeform text into courses.
 *
 * Strategy:
 * 1. Try deterministic parseBulkLine on each line
 * 2. If all lines parse → return without calling LLM
 * 3. If some fail → send entire text to LLM
 *
 * @param {string} text - freeform text to parse
 * @param {Object} options
 * @param {Object} options.provider - LLM provider with generateContent(prompt) method
 * @param {Object} options.app - Firebase app instance (used to create default provider)
 * @returns {{ courses: Object[], method: 'deterministic'|'llm', errors: Object[] }}
 */
export async function parseFreeformText(text, { provider, app } = {}) {
  if (!text || !text.trim()) {
    return { courses: [], method: 'deterministic', errors: [] };
  }

  const lines = text.split('\n');
  const deterministicResults = [];
  const unparsedLines = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;
    const result = parseBulkLine(line);
    if (result) {
      deterministicResults.push(result);
    } else {
      unparsedLines.push(line);
    }
  }

  // If everything parsed deterministically, return without calling LLM
  if (unparsedLines.length === 0) {
    return { courses: deterministicResults, method: 'deterministic', errors: [] };
  }

  // Resolve provider
  const llmProvider = provider || _defaultProvider;
  if (!llmProvider && !app) {
    // No provider available — return deterministic results + errors for unparsed lines
    return {
      courses: deterministicResults,
      method: 'deterministic',
      errors: unparsedLines.map((line) => ({ error: 'No LLM provider available', line })),
    };
  }

  let resolvedProvider = llmProvider;
  if (!resolvedProvider) {
    resolvedProvider = await createFirebaseAIProvider(app);
  }

  // Call LLM with full text (context helps parse ambiguous formats)
  const prompt = buildPrompt(text);
  let responseText;
  try {
    responseText = await resolvedProvider.generateContent(prompt);
  } catch (e) {
    // LLM call failed — return deterministic results + errors
    return {
      courses: deterministicResults,
      method: 'deterministic',
      errors: unparsedLines.map((line) => ({
        error: `LLM call failed: ${e.message}`,
        line,
      })),
    };
  }

  const { courses: llmCourses, errors } = parseLLMResponse(responseText);

  return {
    courses: llmCourses,
    method: 'llm',
    errors,
  };
}
